'use client'

import { useState, useEffect, useCallback, useRef } from 'react'
import { clientLogger } from '@repo/observability/client'
import { useUser } from '../contexts/UserContext'
import { signOutAction } from '../app/actions/auth'

// Timeout configuration (2 hours with 5 minute warning)
const INACTIVITY_TIMEOUT = 2 * 60 * 60 * 1000 // 2 hours
const WARNING_BEFORE_TIMEOUT = 5 * 60 * 1000 // 5 minutes

interface UseInactivityTimeoutResult {
  showWarning: boolean
  remainingTime: number // seconds remaining before logout
  extendSession: () => void
  logout: () => void
}

export function useInactivityTimeout(): UseInactivityTimeoutResult {
  const { profile } = useUser()
  const [showWarning, setShowWarning] = useState(false)
  const [remainingTime, setRemainingTime] = useState(0)

  const lastActivityRef = useRef<number>(Date.now())
  const warningTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const logoutTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const profileRef = useRef(profile)
  const showWarningRef = useRef(showWarning)

  // Keep refs updated
  useEffect(() => {
    profileRef.current = profile
    showWarningRef.current = showWarning
  }, [profile, showWarning])

  // Clear all timers helper
  const clearAllTimers = useCallback(() => {
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current)
      warningTimeoutRef.current = null
    }
    if (logoutTimeoutRef.current) {
      clearTimeout(logoutTimeoutRef.current)
      logoutTimeoutRef.current = null
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current)
      countdownIntervalRef.current = null
    }
  }, [])

  // Start timers
  const startTimers = useCallback(() => {
    clearAllTimers()

    // Set warning timer (shows warning before timeout)
    warningTimeoutRef.current = setTimeout(() => {
      setShowWarning(true)
      setRemainingTime(Math.floor(WARNING_BEFORE_TIMEOUT / 1000))

      // Start countdown
      countdownIntervalRef.current = setInterval(() => {
        setRemainingTime(prev => {
          if (prev <= 1) {
            if (countdownIntervalRef.current) {
              clearInterval(countdownIntervalRef.current)
              countdownIntervalRef.current = null
            }
            return 0
          }
          return prev - 1
        })
      }, 1000)
    }, INACTIVITY_TIMEOUT - WARNING_BEFORE_TIMEOUT)

    // Set logout timer
    logoutTimeoutRef.current = setTimeout(async () => {
      console.log('[InactivityTimeout] Session timed out due to inactivity', {
        userId: profileRef.current?.id,
        lastActivity: new Date(lastActivityRef.current).toISOString()
      })

      // Log to audit via server action
      try {
        const { logSessionTimeout } = await import('../app/actions/auth')
        await logSessionTimeout({
          userId: profileRef.current?.id || '',
          reason: 'inactivity',
          inactivityDuration: INACTIVITY_TIMEOUT / 1000 / 60 // minutes
        })
      } catch (err) {
        console.error('[InactivityTimeout] Failed to log session timeout', err)
        void clientLogger.error(err, {
          componentName: 'useInactivityTimeout',
          action: 'logSessionTimeout',
          feature: 'auth',
          handled: true,
        })
      }

      // Perform logout
      await signOutAction()

      // Redirect to login with timeout reason, preserving current path
      if (typeof window !== 'undefined') {
        const currentPath = window.location.pathname + window.location.search
        window.location.href = `/login?reason=timeout&returnTo=${encodeURIComponent(currentPath)}`
      }
    }, INACTIVITY_TIMEOUT)
  }, [clearAllTimers])

  // Extend session (called when user clicks "Stay logged in")
  const extendSession = useCallback(() => {
    console.log('[InactivityTimeout] Session extended by user')
    setShowWarning(false)
    lastActivityRef.current = Date.now()
    startTimers()
  }, [startTimers])

  // Manual logout
  const logout = useCallback(async () => {
    clearAllTimers()
    await signOutAction()

    // Redirect to login
    if (typeof window !== 'undefined') {
      window.location.href = '/login'
    }
  }, [clearAllTimers])

  // Set up activity listeners and initialize timers
  useEffect(() => {
    if (!profile) return

    // Activity events to track
    const events = [
      'mousedown',
      'mousemove',
      'keydown',
      'scroll',
      'touchstart',
      'click'
    ]

    // Throttle activity updates to avoid excessive calls
    let throttleTimer: NodeJS.Timeout | null = null
    const throttledUpdateActivity = () => {
      if (throttleTimer) return
      throttleTimer = setTimeout(() => {
        lastActivityRef.current = Date.now()
        // Only reset timers if warning is NOT showing
        // When warning is showing, user must click "Stay Logged In" to extend
        if (!showWarningRef.current) {
          startTimers()
        }
        throttleTimer = null
      }, 1000)
    }

    // Add event listeners
    events.forEach(event => {
      window.addEventListener(event, throttledUpdateActivity, { passive: true })
    })

    // Initialize timers
    startTimers()

    // Cleanup
    return () => {
      events.forEach(event => {
        window.removeEventListener(event, throttledUpdateActivity)
      })
      if (throttleTimer) clearTimeout(throttleTimer)
      clearAllTimers()
    }
  }, [profile, startTimers, clearAllTimers])

  return {
    showWarning,
    remainingTime,
    extendSession,
    logout
  }
}
